import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentService, CreateAgentDto } from '../../../services/agent.service';
import { PhoneFormatterService } from '../../../services/phone-formatter.service';

@Component({
  selector: 'app-create-agent',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-agent.component.html',
  styleUrl: './create-agent.component.css'
})
export class CreateAgentComponent {
  @Output() agentCreated = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  agentData: CreateAgentDto = {
    name: '',
    phoneNumber: ''
  };

  isSubmitting = false;
  errors: { [key: string]: string } = {};

  constructor(
    private agentService: AgentService,
    private phoneFormatter: PhoneFormatterService
  ) {}

  validateForm(): boolean {
    this.errors = {};

    // Validate name
    if (!this.agentData.name?.trim()) {
      this.errors['name'] = 'Agent name is required';
    } else if (this.agentData.name.trim().length < 2) {
      this.errors['name'] = 'Agent name must be at least 2 characters long';
    } else if (this.agentData.name.trim().length > 100) {
      this.errors['name'] = 'Agent name must be less than 100 characters';
    }

    // Validate phone number (Indian format)
    if (!this.agentData.phoneNumber?.trim()) {
      this.errors['phoneNumber'] = 'Phone number is required';
    } else {
      const cleanNumber = this.agentData.phoneNumber.replace(/\D/g, '');
      // Indian mobile numbers: 10 digits starting with 6,7,8,9 or with country code +91
      if (!/^(\+91|91)?[6-9]\d{9}$/.test(cleanNumber)) {
        this.errors['phoneNumber'] = 'Please enter a valid Indian mobile number (10 digits starting with 6,7,8,9)';
      }
    }

    return Object.keys(this.errors).length === 0;
  }

  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;

    // Prepare data for submission - trim name and normalize phone number
    const trimmedData: CreateAgentDto = {
      name: this.agentData.name.trim(),
      phoneNumber: this.phoneFormatter.getStorageFormat(this.agentData.phoneNumber)
    };

    this.agentService.createAgent(trimmedData).subscribe({
      next: () => {
        this.agentCreated.emit();
        this.resetForm();
      },
      error: (error) => {
        console.error('Error creating agent:', error);
        this.handleServerErrors(error);
        this.isSubmitting = false;
      }
    });
  }

  private handleServerErrors(error: any): void {
    this.errors = {};

    if (error?.error?.errors) {
      // Handle validation errors from the server
      Object.keys(error.error.errors).forEach(key => {
        const fieldName = key.toLowerCase();
        if (error.error.errors[key] && Array.isArray(error.error.errors[key])) {
          this.errors[fieldName] = error.error.errors[key][0];
        } else if (error.error.errors[key]) {
          this.errors[fieldName] = error.error.errors[key];
        }
      });
    } else if (error?.error?.message) {
      this.errors['general'] = error.error.message;
    } else if (error?.message) {
      this.errors['general'] = error.message;
    } else {
      this.errors['general'] = 'An unexpected error occurred. Please try again.';
    }
  }

  onCancel(): void {
    this.closeModal.emit();
    this.resetForm();
  }

  private resetForm(): void {
    this.agentData = {
      name: '',
      phoneNumber: ''
    };
    this.errors = {};
    this.isSubmitting = false;
  }

  // Helper method to check if a field has an error
  hasError(fieldName: string): boolean {
    return !!this.errors[fieldName];
  }

  // Helper method to get error message for a field
  getError(fieldName: string): string {
    return this.errors[fieldName] || '';
  }

  // Format phone number as user types (Indian format)
  onPhoneNumberInput(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    
    // Handle country code +91
    if (value.startsWith('91') && value.length > 2) {
      value = '+91 ' + value.substring(2);
    } else if (value.startsWith('91') && value.length <= 2) {
      value = '+91';
    } else if (value.length > 0 && !value.startsWith('91')) {
      // Format Indian mobile number: +91 XXXXX XXXXX
      if (value.length > 5) {
        value = '+91 ' + value.substring(0, 5) + ' ' + value.substring(5, 10);
      } else if (value.length > 0) {
        value = '+91 ' + value;
      }
    }
    
    // Limit to 10 digits (excluding country code)
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length > 12) { // +91 + 10 digits
      return; // Don't update if too long
    }
    
    this.agentData.phoneNumber = value;
  }

  // Handle modal backdrop click
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }
}
